import { getStorage, setStorage, removeStorage } from '@/utils/storage'
import { getTelegramWebApp, isTelegramWebAppActive } from '@/utils/telegramWebAppClient'
import logger from '@/utils/logger'

function getCloudStorage() {
  return getTelegramWebApp()?.CloudStorage ?? null
}

function cloudGetItem(key) {
  const cloud = getCloudStorage()
  if (!cloud?.getItem) return Promise.resolve(null)

  return new Promise((resolve) => {
    cloud.getItem(key, (error, value) => {
      if (error) {
        logger.debug(`CloudStorage getItem failed for "${key}":`, error)
        resolve(null)
        return
      }
      resolve(value ?? null)
    })
  })
}

function cloudSetItem(key, value) {
  const cloud = getCloudStorage()
  if (!cloud?.setItem) return Promise.resolve(false)

  return new Promise((resolve) => {
    cloud.setItem(key, value, (error) => {
      if (error) {
        logger.debug(`CloudStorage setItem failed for "${key}":`, error)
        resolve(false)
        return
      }
      resolve(true)
    })
  })
}

function cloudRemoveItem(key) {
  const cloud = getCloudStorage()
  if (!cloud?.removeItem) return Promise.resolve(false)

  return new Promise((resolve) => {
    cloud.removeItem(key, (error) => {
      if (error) {
        logger.debug(`CloudStorage removeItem failed for "${key}":`, error)
        resolve(false)
        return
      }
      resolve(true)
    })
  })
}

function parseStoredValue(value, defaultValue) {
  if (value === null || value === undefined || value === '') {
    return defaultValue
  }
  if (typeof defaultValue === 'string') {
    return String(value)
  }
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function serializeValue(value) {
  return typeof value === 'string' ? value : JSON.stringify(value)
}

/**
 * Read a preference in Telegram CloudStorage when available, otherwise localStorage.
 */
export async function getTelegramCloudItem(key, defaultValue = null) {
  if (!isTelegramWebAppActive()) {
    return getStorage(key, defaultValue)
  }

  try {
    const cloudValue = await cloudGetItem(key)
    if (cloudValue !== null && cloudValue !== undefined && cloudValue !== '') {
      const parsed = parseStoredValue(cloudValue, defaultValue)
      setStorage(key, parsed)
      return parsed
    }
  } catch (error) {
    logger.debug(`Failed to read CloudStorage key "${key}":`, error)
  }

  return getStorage(key, defaultValue)
}

/**
 * Persist a preference to localStorage and Telegram CloudStorage when available.
 */
export async function setTelegramCloudItem(key, value) {
  setStorage(key, value)

  if (!isTelegramWebAppActive()) {
    return true
  }

  try {
    return await cloudSetItem(key, serializeValue(value))
  } catch (error) {
    logger.debug(`Failed to write CloudStorage key "${key}":`, error)
    return false
  }
}

export async function removeTelegramCloudItem(key) {
  removeStorage(key)

  if (!isTelegramWebAppActive()) {
    return true
  }

  try {
    return await cloudRemoveItem(key)
  } catch (error) {
    logger.debug(`Failed to remove CloudStorage key "${key}":`, error)
    return false
  }
}
